import React, { useState, useRef, useEffect } from 'react';
import { FaAngleDown, FaAngleUp } from 'react-icons/fa';
import { Body, BodyType, BodyColor } from '../../../ui-elements';

interface YearOption {
    label: string;
    value: number;
}

interface PoiGrowthHeaderProps {
    selectedYear: number;
    options: YearOption[];
    onYearChange?: (year: number) => void;
}

export const PoiGrowthHeader: React.FC<PoiGrowthHeaderProps> = ({
    selectedYear,
    options,
    onYearChange
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    // Get display label for selected year
    const selectedOption = options.find(opt => opt.value === selectedYear);
    const displayLabel = selectedOption
        ? `PoI growth (${selectedOption.label})`
        : `PoI growth (${selectedYear} ${selectedYear > 1 ? 'years' : 'year'})`;

    // Close dropdown on outside click
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleYearSelect = (year: number) => {
        onYearChange?.(year);
        setIsOpen(false);
    };

    return (
        <div className="position-relative" ref={dropdownRef}>
            <div
                className="d-flex align-items-center gap-1 cursor-pointer"
                onClick={(e) => {
                    e.stopPropagation();
                    if (options.length > 0) {
                        setIsOpen(!isOpen);
                    }
                }}
            >
                <span style={{ whiteSpace: 'nowrap' }}>{displayLabel}</span>
                {options.length > 0 && (
                    <span className="material-symbols-outlined fs-14 text-black cursor-pointer">
                        {isOpen ? "keyboard_arrow_up" : "keyboard_arrow_down"}
                    </span>
                )}
            </div>

            {/* Dropdown */}
            {isOpen && options.length > 0 && (
                <div
                    style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        background: '#fff',
                        border: '1px solid #e0e0e0',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                        borderRadius: 8,
                        zIndex: 100,
                        minWidth: 150,
                        padding: 12,
                        marginTop: 4
                    }}
                >
                    {options.map((option) => (
                        <label
                            key={option.value}
                            className='d-flex align-items-center gap-2 cursor-pointer p-0 padding-top-bottom-2'
                        >
                            <input
                                type="radio"
                                name="poi-growth-year"
                                checked={selectedYear === option.value}
                                onChange={() => handleYearSelect(option.value)}
                            />
                            <Body
                                type={BodyType.p3}
                                color={BodyColor.secondary}
                                classname="p-0"
                            >
                                {option.label}
                            </Body>
                        </label>
                    ))}
                </div>
            )}
        </div>
    );
};
